import React, { useState, useEffect } from 'react';
import { Button, Modal, Spinner } from 'react-bootstrap-5';
import { getPdfTemplateUsages, type PdfTemplateUsage } from '../api';

interface DeletePdfModalProps {
  basePath: string;
  pdfId: number;
  show: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
}

export default function DeletePdfModal({ basePath, pdfId, show, onClose, onConfirm }: DeletePdfModalProps) {
  const [deleting, setDeleting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [usages, setUsages] = useState<PdfTemplateUsage[]>([]);

  useEffect(() => {
    if (show) {
      setLoading(true);
      getPdfTemplateUsages(basePath, pdfId)
        .then(setUsages)
        .finally(() => setLoading(false));
    }
  }, [show, basePath, pdfId]);

  const handleConfirm = async () => {
    setDeleting(true);
    try {
      await onConfirm();
      onClose();
    } finally {
      setDeleting(false);
    }
  };

  const renderModalContents = () => {
    if (usages.length > 0) {
      return (
        <>
          <p>
            This PDF is used in <u>{usages.length}</u>
            {usages.length === 1 ? ' document default ' : ' document defaults '}
            and will be removed from:
          </p>
          <ul className="mb-0" style={{ maxHeight: '200px', overflowY: 'auto' }}>
            {usages.map(usage => (
              <li key={usage.documentOptionsId}>
                <strong>{usage.parentName}</strong> - {usage.documentTemplateName}
              </li>
            ))}
          </ul>
          <p className="mt-4 mb-0">
            Note: Previously created documents won't be affected.
          </p>
        </>
      )
    } else {
      return (
        <p>This PDF is not currently used in any document templates.</p>
      )
    }
  };

  return (
    <Modal centered show={show} onHide={onClose}>
      <Modal.Header closeButton>
        <Modal.Title>Delete PDF Template</Modal.Title>
        <p className="mb-0 text-muted">Are you sure you want to remove this template?</p>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="text-center py-3">
            <Spinner animation="border" size="sm" /> Checking for usages...
          </div>
        ) : renderModalContents()}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="cancel" onClick={onClose}>
          Cancel
        </Button>
        <Button disabled={deleting || loading} variant="danger" onClick={handleConfirm}>
          {deleting ? 'Deleting...' : 'Delete'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
